// components/CartAdmin.jsx
import React, { useState, useEffect } from 'react';
import CartSection from '../components/CartSection';
import axios from "axios";
import config from '../config/config';

const CartAdmin = () => {
  const [activeDesign, setActiveDesign] = useState('sidebar'); // 'sidebar', 'bottom', 'center'
  const [title, setTitle] = useState('Your Cart');
  const [emptyMessage, setEmptyMessage] = useState('Your cart is empty. Start shopping!');
  const [continueShoppingText, setContinueShoppingText] = useState('Continue Shopping');
  const [checkoutText, setCheckoutText] = useState('Proceed to Checkout');
  const [devMode, setDevMode] = useState(false);

  const [background, setBackground] = useState('bg-white');
  const [textColor, setTextColor] = useState('text-gray-900');
  const [buttonBg, setButtonBg] = useState('bg-gray-900');
  const [buttonText, setButtonText] = useState('text-white');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const load = async () => {
      if (!token) return setLoading(false);

      try {
        setLoading(true);
        await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

        const res = await axios.get(config.API_URL + '/api/pullCartSettings', {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });

        const db = res.data.cartSettings || {};

        setActiveDesign(db.activeDesign || 'sidebar');
        setTitle(db.title || 'Your Cart');
        setEmptyMessage(db.emptyMessage || 'Your cart is empty. Start shopping!');
        setContinueShoppingText(db.continueShoppingText || 'Continue Shopping');
        setCheckoutText(db.checkoutText || 'Proceed to Checkout');
        setDevMode(db.devMode || false);

        const styles = db.customStyles || {};
        setBackground(styles.background || 'bg-white');
        setTextColor(styles.textColor || 'text-gray-900');
        setButtonBg(styles.buttonBg || 'bg-gray-900');
        setButtonText(styles.buttonText || 'text-white');
      } catch (err) {
        console.error("Load failed:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token]);

  const currentConfig = {
    activeDesign,
    title,
    emptyMessage,
    continueShoppingText,
    checkoutText,
    devMode,
    customStyles: { background, textColor, buttonBg, buttonText },
  };

  const save = async () => {
    try {
      setSaving(true);
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrf = document.cookie.split('; ').find(r => r.startsWith('XSRF-TOKEN='))?.split('=')[1];

      await axios.post(config.API_URL + "/api/saveCartSettings", { settings: currentConfig }, {
        headers: {
          "Content-Type": "application/json",
          "X-XSRF-TOKEN": decodeURIComponent(xsrf || ''),
        },
        withCredentials: true,
      });

      alert("Cart settings saved! 🎉");
    } catch (err) {
      alert("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Preview Area */}
      <div className="flex-1 p-8">
        <h1 className="text-3xl font-bold mb-8">Cart Modal Editor</h1>

        {/* Design Selector */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Choose Design</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { value: 'sidebar', label: 'Right Sidebar', desc: 'Classic desktop + mobile full' },
              { value: 'bottom', label: 'Bottom Sheet', desc: 'Mobile-first, app-like' },
              { value: 'center', label: 'Center Modal', desc: 'Premium focused view' },
            ].map(d => (
              <button
                key={d.value}
                onClick={() => setActiveDesign(d.value)}
                className={`p-6 border-2 rounded-xl text-left transition-all ${
                  activeDesign === d.value 
                    ? 'border-blue-500 bg-blue-50 shadow-md' 
                    : 'border-gray-200 hover:border-gray-300 hover:shadow'
                }`}
              >
                <h3 className="font-medium text-lg">{d.label}</h3>
                <p className="text-sm text-gray-600 mt-1">{d.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Preview Button */}
        <button
          onClick={() => setPreviewOpen(true)}
          className="bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700"
        >
          Preview Selected Design
        </button>
      </div>

      {/* Settings Sidebar */}
      <div className="w-96 bg-white border-l p-6 overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6">Customize Cart</h2>

        <div className="space-y-6">
          <div>
            <label className="block font-medium mb-2">Modal Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} className="w-full p-3 border rounded-lg" />
          </div>

          <div>
            <label className="block font-medium mb-2">Empty Cart Message</label>
            <input value={emptyMessage} onChange={e => setEmptyMessage(e.target.value)} className="w-full p-3 border rounded-lg" />
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block font-medium mb-2">Continue Shopping Text</label>
              <input value={continueShoppingText} onChange={e => setContinueShoppingText(e.target.value)} className="w-full p-3 border rounded-lg" />
            </div>
            <div>
              <label className="block font-medium mb-2">Checkout Button Text</label>
              <input value={checkoutText} onChange={e => setCheckoutText(e.target.value)} className="w-full p-3 border rounded-lg" />
            </div>
          </div>

          {/* Dev Mode */}
          <div className="bg-yellow-50 p-5 rounded-xl border border-yellow-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold">Custom Styling (Dev Mode)</h3>
              <button 
                onClick={() => setDevMode(!devMode)}
                className={`w-11 h-6 rounded-full relative ${devMode ? 'bg-yellow-600' : 'bg-gray-300'}`}
              >
                <span className={`absolute w-4 h-4 bg-white rounded-full transition ${devMode ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {devMode && (
              <div className="space-y-4">
                <input value={background} onChange={e => setBackground(e.target.value)} placeholder="Background" className="w-full p-3 border rounded" />
                <input value={textColor} onChange={e => setTextColor(e.target.value)} placeholder="Text Color" className="w-full p-3 border rounded" />
                <input value={buttonBg} onChange={e => setButtonBg(e.target.value)} placeholder="Button Background" className="w-full p-3 border rounded" />
                <input value={buttonText} onChange={e => setButtonText(e.target.value)} placeholder="Button Text" className="w-full p-3 border rounded" />
              </div>
            )}
          </div>

          <button
            onClick={save}
            disabled={saving}
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold hover:bg-blue-700 disabled:opacity-60 mt-8"
          >
            {saving ? 'Saving...' : 'Save Cart Settings'}
          </button>
        </div>
      </div>

      {/* Live Preview Modal */}
      {previewOpen && (
        <CartSection 
          isOpen={previewOpen} 
          onClose={() => setPreviewOpen(false)} 
          config={currentConfig} 
        />
      )}
    </div>
  );
};

export default CartAdmin;